import React from 'react';

/**
 * Bilingual — displays a local-language line on top and the English line
 * below in smaller, muted, italic text. Chairman's bilingual pedagogy
 * (Sprint 10.6.7): the student sees the local-language version for
 * comprehension AND the English version for language acquisition, together
 * on the same card — not one or the other.
 *
 * Fallbacks:
 * - No local text (empty / missing / same as English) → renders the English
 *   line at normal weight only.
 * - This keeps the component safe to sprinkle over any English string; when
 *   the app is in English-only mode it is a no-op wrapper.
 *
 * Props:
 * - local:      string  the translated line (may be null / empty)
 * - english:    string  the English line
 * - className:  extra classes applied to BOTH lines' primary tag when
 *               bilingual, or the single English line when English-only.
 * - localAs:    tag for the local line (default 'p')
 * - englishAs:  tag for the English line (default 'p')
 * - testId:     data-testid root; when bilingual the local line gets
 *               `${testId}-local` and the English line gets `${testId}-english`.
 * - englishClassName:  override the muted styling of the English line
 *                      (mainly for `Try.js` where the English line inside
 *                      certain cards needs a bit more contrast).
 */
export default function Bilingual({
  local,
  english,
  className = '',
  localAs = 'p',
  englishAs = 'p',
  testId,
  englishClassName = 'text-sm italic text-muted-foreground',
}) {
  const LocalTag = localAs;
  const EnglishTag = englishAs;

  const trimmedLocal = typeof local === 'string' ? local.trim() : '';
  const trimmedEnglish = typeof english === 'string' ? english.trim() : '';
  const hasLocal = trimmedLocal.length > 0 && trimmedLocal !== trimmedEnglish;

  if (!hasLocal) {
    return (
      <EnglishTag className={className} data-testid={testId}>
        {english}
      </EnglishTag>
    );
  }

  return (
    <div className="space-y-1" data-testid={testId}>
      <LocalTag
        className={className}
        data-testid={testId ? `${testId}-local` : undefined}
        lang="auto"
      >
        {local}
      </LocalTag>
      <EnglishTag
        className={englishClassName}
        data-testid={testId ? `${testId}-english` : undefined}
        lang="en"
      >
        {english}
      </EnglishTag>
    </div>
  );
}

/**
 * Resolves the current i18n language to a bilingual overlay code.
 * Returns 'si', 'ta', or null.
 */
export function bilingualOverlayCode(lang) {
  if (!lang) return null;
  const base = String(lang).toLowerCase();
  if (base === 'si' || base === 'si-en-dual') return 'si';
  if (base === 'ta' || base === 'ta-en-dual') return 'ta';
  return null;
}
